const moment = require("moment");
const { isPartner } = require("../../../helpers/commons.helpers");
/**
 * @fileoverview Reservation Format Service
 * 
 * This module provides utility functions for formatting various types of data
 * related to reservations, bookings, and travel services. All functions handle
 * data transformation from internal codes/values to user-friendly display strings.
 * 
 * @module format.service
 * @namespace ReservationFormatters
 * @author RhomTrip Development Team
 */

/**
 * Formats payment type codes into user-friendly display strings
 * 
 * Converts internal payment type identifiers to readable payment method names
 * for display in user interfaces, reports, and documentation.
 * 
 * @function formatPaymentType
 * @param {string} type - The payment type code to format
 * @returns {string} The formatted payment type display name
 */
export function formatPaymentType(type) {
    if (type === "ota") {
        return "OTA";
    } else if (type === "card") {
        return "Credit Card";
    } else if (type === "transfer") {
        return "Bank Transfer";
    } else if (type === "credit") {
        return "Credit";
    }
    return "Other";
}

/**
 * Formats service category codes into user-friendly class descriptions
 * 
 * Converts internal service category identifiers to readable service class names
 * for display in booking interfaces and service descriptions.
 * 
 * @function formatServiceCategory
 * @param {string} type - The service category code to format
 * @returns {string} The formatted service category display name
 */
export function formatServiceCategory(type) {
    if (type === "standard") {
        return "Standard Class";
    } else if (type === "business") {
        return "Business Class";
    } else {
        return "Luxury Class";
    }
}
/**
 * Formats transfer type codes into user-friendly transfer descriptions
 * 
 * Converts internal transfer type identifiers to readable transfer service names
 * for display in booking interfaces and service selection menus.
 * 
 * @function formatTransferType
 * @param {string} type - The transfer type code to format
 * @returns {string|undefined} The formatted transfer type display name, or undefined for unknown types
 */
export function formatTransferType(type) {
    if (type === "airport") {
        return "Airport Transfer";
    } else if (type === "arrival") {
        return "Airport Transfer (Arrival)";
    } else if (type === "departure") {
        return "Airport Transfer (Departure)";
    } else if (type === "station") {
        return "Station Transfer";
    } else if (type === "port") {
        return "Port Transfer";
    } else if (type === "hourly") {
        return "Hourly";
    } else if (type === "journey") {
        return "Journey";
    }
}

/**
 * Formats client type codes into user-friendly client category descriptions
 * 
 * Converts internal client type identifiers to readable client category names
 * for display in customer management interfaces and reporting systems.
 * 
 * @function formatClientType
 * @param {string} type - The client type code to format
 * @returns {string} The formatted client type display name
 */
export function formatClientType(type) {
    if (type === "customer") {
        return "Customer";
    } else if (type === "travel_agency") {
        return "Travel Agency";
    } else if (type === "business") {
        return "Business";
    } else if (type === "travel_access") {
        return "Agent";
    } else if (type === "guest") {
        return "Guest";
    } else if (type === "ota") {
        return "Reseller";
    } else if (type === "e-commerce") {
        return "Reseller";
    }
    return "Customer";
}

/**
 * Formats passenger type codes into user-friendly passenger category descriptions
 *
 * Converts internal passenger type identifiers to readable passenger category names
 * for display in booking interfaces and service selection menus.
 *
 * @function formatPassengerType
 * @param {string} type - The passenger type code to format
 * @returns {string} The formatted passenger type display name
 */
export function formatPassengerType(type) {
    if (type === "ADULT") {
        return "Adult";
    } else if (type === "SENIOR") {
        return "Senior";
    } else {
        return "Child";
    }
}

/** * Formats position type codes into user-friendly position descriptions
 *
 * Converts internal position type identifiers to readable position names
 * for display in employee management interfaces and reporting systems.
 *
 * @function formatPosition
 * @param {string} type - The position type code to format
 * @returns {string} The formatted position display name
 */
export function formatPosition(type) {
    if (type === "administration-finance") {
        return " Administration & Finance";
    } else if (type === "co-founder-cto") {
        return "Co-Founder & CTO";
    } else if (type === "co-founder-coo") {
        return "Co-Founder & COO";
    } else if (type === "co-founder-ceo") {
        return "Co-Founder & CEO";
    } else if (type === "customer-service-representative") {
        return "CSR";
    } else if (type === "data-entry-operator") {
        return "Data Entry Operator";
    } else if (type === "designer-front-end") {
        return "Designer & Front End";
    } else if (type === "developer") {
        return "Developer";
    } else if (type === "junior-reservations-sales-agent") {
        return "Junior Reservations & Sales Agent";
    } else if (type === "product-manager") {
        return "Product Manager";
    } else if (type === "reservations-csr-manager") {
        return "Reservations & CSR Manager";
    } else if (type === "reservations-sales-agent") {
        return "Reservations & Sales Agent";
    } else if (type === "sales-manager") {
        return "Sales Manager";
    } else if (type === "account-manager") {
        return "Account Manager";
    } else if (type == "customer-service-manager") {
        return "Customer Service Manager";
    } else if (type === "customer-service-claims-manager") {
        return "Customer Service & Claims Manager";
    }
    return "Unknown";
}

/** * Formats transport type codes into user-friendly transport label descriptions
 *
 * Converts internal transport type identifiers to readable labels for display
 * in booking interfaces and service selection menus.
 *
 * @function formatTransportTypeLabel
 * @param {string} type - The transport type code to format
 * @returns {string} The formatted transport type label
 */
export function formatTransportTypeLabel(type) {
    if (["airport", "arrival", "departure"].includes(type)) {
        return "Flight N°:";
    } else if (type === "station") {
        return "Rail Line:";
    } else if (type === "port") {
        return "Cruise Line:";
    } else if (type === "hourly") {
        return "Hours:";
    }
    return "";
}

/** * Formats booking type codes into user-friendly booking type descriptions
 *
 * Converts internal booking type identifiers to readable booking type names
 * for display in booking interfaces and service selection menus.
 *
 * @function formatBookingTypeLabel
 * @param {string} type - The booking type code to format
 * @returns {string} The formatted booking type label
 */
export function formatBookingTypeLabel(type) {
    if (type === "transfer") {
        return "Base Transfer";
    } else if (type === "experience") {
        return "Base Experience";
    }
    return "Base Service";
}

/** * Formats a price amount to two decimal places as a string.
 * @param {number} amount - The price amount to format.
 * @returns {string} - The formatted price amount as a string.
 */
export function formatPriceAmount(amount) {
    return amount.toFixed(2);
}

/**
 * Format a booking date to "DD-MMM-YYYY" format
 * @param {string} date - The date string to format
 * @returns {string} - The formatted date string
 */
export function formatBookingDate(date) {
    if (date) {
        return moment.parseZone(date).format("DD-MMM-YYYY");
    }
    return "";
}

/** Format a booking time to "hh:mm A" format
 * @param {string} time - The time string to format
 * @returns {string} - The formatted time string
 */
export function formatBookingTime(time) {
    return moment(time, ["hh:mm A"]).format("hh:mm A");
}

/** Build vehicle category label for display.
 * @param {Object} vehicle - The vehicle object containing category and seats information.
 * @returns {string} - The formatted vehicle category label.
 */


/** * Gets the quote type constant based on the provided type string.
 * @param {string} type - The quote type string.
 * @returns {string|undefined} - The corresponding quote type constant.
 */
export function getQuoteType(type) {
    if (type === "arrival") {
        return "ARRIVAL";
    } else if (type === "departure") {
        return "DEPARTURE";
    } else if (type === "station") {
        return "STATION";
    } else if (type === "port") {
        return "PORT";
    } else if (type === "hourly") {
        return "HOURLY";
    } else if (type === "journey") {
        return "JOURNEY";
    }
}

/** Get transport reference according the booking type.
* @param {string} booking - The  booking all data
* @returns {string} - The transport reference number according to booking type ("arrival", "departure","station", "port").
*/
export function getQuoteRouterReference(booking) {
    if (["arrival", "departure"].includes(booking.type)) {
        return booking.flight_number;
    } else if (booking.type === "station") {
        return booking.number_stations;
    } else if (booking.type === "port") {
        return booking.cruise_line;
    } else if (booking.type === "hourly") {
        return booking.number_hourly;
    }
    return "";
}

/** 
 * Format vehicle category for display.
 * @param {string} category - The vehicle category ("standard", "business", "luxury").
 * @returns {string} - The formatted vehicle category.
 */
export function formatVehicleCategory(category) {
    if (category === "standard") {
        return "Standard Class";
    } else if (category === "business") {
        return "Business Class";
    } else {
        return "Luxury Class";
    }
}




/**
 * Format date for display.
 * @param {Date} date - The date to format.
 * @returns {string} - The formatted date.
 */
export function formatDate(date) {
    return moment(date)
        .utc(true)
        .format("DD-MMM-YYYY");
}

/** Format time for display.
 * @param {string} time - The time to format.
 * @returns {string} - The formatted time.
 */
export function formatTime(time) {
    return moment(time, ["hh:mm A"]).format("hh:mm A");
}


/** Format pickup date to "YYYY-MM-DD" format
 * @param {string} date - The date string to format
 * @returns {string} - The formatted date string
 */
export function formatPickupDate(date) {
    if (date) {
        return moment.parseZone(date).format("YYYY-MM-DD");
    }
    return "";
}


/** Get label of the transport reference according the booking type.
 * @param {string} bookingType - The type of booking ("arrival", "departure","station", "port").
 * @returns {string} - The transport reference label.
 */
export function getTransportReferenceLabel(bookingType) {
    if (["airport", "arrival", "departure"].indexOf(bookingType) > -1) {
        return "Flight N°";
    } else if (bookingType === "station") {
        return "Rail Line";
    } else if (bookingType === "port") {
        return "Cruise Ship";
    } else if (bookingType === "hourly") {
        return "Hours";
    }
    return "";
}

/** Get transport reference according the booking type.
 * @param {string} booking - The  booking all data
 * @returns {string} - The transport reference number according to booking type ("arrival", "departure","station", "port").
 */
export function getTransportReferenceValue(booking) {
    if (["airport", "arrival", "departure"].includes(booking.type)) {
        return booking.flight_number;
    } else if (booking.type === "station") {
        return booking.number_stations;
    } else if (booking.type === "port") {
        return booking.cruise_line;
    } else if (booking.type === "hourly") {
        return `${booking.number_hourly} hours`;
    }
    return "";
}

/** Build vehicle category label for display.
 * @param {Object} vehicle - The vehicle object containing category and seats information.
 * @returns {string} - The formatted vehicle category label.
 */
export function buildVehicleCategoryLabel(vehicle) {
    if (!vehicle) return "";
    const { category, seats } = vehicle;
    if (category === "standard") {
        return `Standard Class ${seats} Seats`;
    } else if (category === "luxury") {
        return `Luxury Class ${seats} Seats`;
    } else if (category === "business") {
        return `Business Class ${seats} Seats`;
    }
    return "";
}

/** Build vehicle display name for display.
 * @param {Object} vehicle - The vehicle object containing make information.
 * @returns {string} - The formatted vehicle display name.
 */
export function buildVehicleDisplayName(vehicle) {
    if (!vehicle) return "";
    const { make_label } = vehicle;
    return `${make_label} or similar`;
}

/**
 * Returns the icon class for a location type.
 * @param {string} type - The location type ("airport", "lodging", "point_of_interest", "route", "train_station").
 * @returns {string} - The icon CSS class.
 */
export function getIcon(type) {
    if (type === "airport") {
        return "flaticon-bold-plane";
    } else if (type === "lodging") {
        return "flaticon-bold-bed";
    } else if (type === "point_of_interest") {
        return "flaticon-bold-map";
    } else if (type === "route") {
        return "flaticon-bold-distance";
    } else if (type === "train_station" || type === "transit_station") {
        return "flaticon-bold-train";
    } else {
        return "flaticon-bold-map";
    }
}

/**
 * Builds the display label for a customer, including the company name for agency/partner types.
 * @param {Object} customer - The customer object.
 * @returns {string} - The display label.
 */
export function getCustomerLabel(customer) {
    if (isPartner(customer.type)) {
        return `${customer.company_name} - ${customer.first_name} ${customer.last_name}`;
    }
    return `${customer.first_name} ${customer.last_name}`;
}

/**
 * Formats a numeric price to two decimal places.
 * @param {number|string} price - The price to format.
 * @returns {string} - The formatted price.
 */
export function parsePrice(price) {
    return Number.parseFloat(price).toFixed(2);
}

/**
 * Returns a salutation title (Mr./Mrs./Mx.) based on gender.
 * @param {string} gender - The gender ("male", "female", or other).
 * @returns {string} - The salutation title.
 */
export function getTitleByGender(gender) {
    if (gender === "male") {
        return "Mr.";
    } else if (gender === "female") {
        return "Mrs.";
    } else {
        return "Mx.";
    }
}

/**
 * Returns the appropriate reference-column header label based on transfer type.
 * @param {string} type - The transfer type.
 * @returns {string} - The header label (Flight N°, Rail Line, Cruise Ship, or empty).
 */
export function getLabelToHeaderTable(type) {
    if (["airport", "arrival", "departure"].indexOf(type) > -1) {
        return "Flight N°";
    } else if (type === "station") {
        return "Rail Line";
    } else if (type === "port") {
        return "Cruise Ship";
    } else {
        return "";
    }
}

/**
 * Filters/adjusts the passenger table's column definitions based on transfer type
 * (e.g., hiding itinerary column for hourly/journey and localizing its header label).
 * @param {Array} files - The passenger table column definitions.
 * @param {string} transferType - The current transfer type.
 * @returns {Array} - The adjusted column definitions.
 */
export function getHeaderPassengerTable(files = [], transferType) {
    return files
        .filter(file => {
            if (["hourly", "journey"].includes(transferType)) {
                return file.key !== "itinerary";
            } else {
                return true;
            }
        })
        .map(file => {
            if (file.key === "itinerary") {
                file.label = getLabelToHeaderTable(transferType);
            }
            return file;
        });
}

/**
 * Normalizes a phone number string to a leading "+" plus digits format.
 * @param {string} contactNumber - The raw contact number.
 * @returns {string} - The normalized contact number.
 */
export function parserContactNumber(contactNumber) {
    if (contactNumber) {
        contactNumber = contactNumber.replace(/[^0-9+]/g, "");
        if (contactNumber.charAt(0) !== "+") {
            contactNumber = `+${contactNumber}`;
        }
        return contactNumber;
    } else {
        return "";
    }
}

/**
 * Estimates a textarea's row count from the text length.
 * @param {string} value - The text content.
 * @returns {number} - The suggested number of rows.
 */
export function adjustHeight(value) {
    return Math.ceil(value.length / 60);
}

/**
 * Finds a phone number matching the given preference flag.
 * @param {boolean} status - The preferred flag to match.
 * @param {Array} phoneNumbers - The list of phone numbers to search.
 * @returns {string} - The matching phone number, or empty string if none found.
 */
export function getContactNumber(status, phoneNumbers = []) {
    let contactNumber = phoneNumbers.find(
        phone => phone.preferred === status
    );
    if (contactNumber) {
        return contactNumber.number;
    } else {
        return "";
    }
}

/**
 * Formats a duration unit and value into a pluralized display label.
 * @param {string} unit - The unit code ("HOURS", "MINUTES", "DAYS").
 * @param {number} value - The duration value.
 * @returns {string} - The pluralized unit label.
 */
export function getUnitTime(unit, value) {
    if ("HOURS" === unit) {
        return value > 1 ? "hours" : "hour";
    } else if ("MINUTES" === unit) {
        return value > 1 ? "Minutes" : "Minute";
    } else if ("DAYS" === unit) {
        return value > 1 ? "days" : "day";
    } else {
        return "";
    }
}

/**
 * Returns the origin label of a reservation source.
 * @param {Object} source - The reservation's source object.
 * @returns {string} - The source origin, or empty string if none.
 */
export function getSourceOrigin(source) {
    if (source) {
        return source.origin;
    }
    return "";
}

/**
 * Returns the display name of the service provider for a reservation source.
 * @param {Object} source - The reservation's source object.
 * @returns {string} - "Ventrata", "RhomTrip", or empty string if no source.
 */
export function getServiceProvider(source) {
    if (source) {
        if (source.provider === "VENTRATA") {
            return "Ventrata";
        } else {
            return "RhomTrip";
        }
    }
    return "";
}

/**
 * Builds the display name of the reservation's responsible party.
 * @param {Object} responsible - The { type, ref } responsible reference.
 * @returns {string} - The responsible party's display name.
 */
export function getResponsibleName(responsible) {
    try {
        if (responsible.type === "Customers") {
            return `${responsible.ref.first_name} ${responsible.ref.last_name}`;
        } else if (responsible.type === "Operators") {
            return `${responsible.ref.contactPerson.firstName} ${responsible.ref.contactPerson.lastName}`;
        } else if (responsible.type === "Users") {
            return `${responsible.ref.personal_info.first_name} ${responsible.ref.personal_info.last_name}`;
        } else {
            return "Unknown";
        }
    } catch (error) {
        return "Unknown";
    }
}

/**
 * Builds the display label for the reservation's responsible party's position/role.
 * @param {Object} responsible - The { type, ref } responsible reference.
 * @returns {string} - The responsible party's position label.
 */
export function getResponsiblePosition(responsible) {
    try {
        if (responsible.type === "Customers") {
            return formatClientType(responsible.ref.type);
        } else if (responsible.type === "Operators") {
            return "Supplier";
        } else if (responsible.type === "Users") {
            return formatPosition(responsible.ref.agreement.position);
        } else {
            return "Unknown";
        }
    } catch (error) {
        return "Unknown";
    }
}

